/**
 * Lucent Carat Lab - Unified Luxury Wishlist Manager
 * Handles:
 * - LocalStorage persistence with full product metadata & backward-compatible ID migration
 * - Live header badge synchronization (desktop & mobile)
 * - Interactive slide-out Wishlist Drawer with "Add to Bag" & "Remove" actions
 * - Real-time sync with product card hearts
 * - Full standalone Wishlist Page rendering
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'lucent_wishlist_items';

  window.LucentWishlist = {
    KEY: STORAGE_KEY,

    getItems: function () {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.map(function (item) {
          if (typeof item === 'string' || typeof item === 'number') {
            return {
              id: String(item),
              handle: '',
              title: 'Saved Piece',
              price: '',
              compare_at_price: '',
              image: '',
              url: ''
            };
          }
          return {
            id: String(item.id || item.productId || ''),
            handle: item.handle || '',
            title: item.title || 'Saved Piece',
            price: item.price || '',
            compare_at_price: item.compare_at_price || '',
            image: item.image || '',
            url: item.url || (item.handle ? '/products/' + item.handle : ''),
            variantId: item.variantId || ''
          };
        }).filter(function (i) { return Boolean(i.id); });
      } catch (e) {
        console.warn('LucentWishlist: failed to read items', e);
        return [];
      }
    },

    saveItems: function (items) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {
        console.warn('LucentWishlist: failed to save items', e);
      }
    },

    has: function (productId) {
      if (!productId) return false;
      const strId = String(productId);
      return this.getItems().some(function (item) {
        return item.id === strId || item.handle === strId;
      });
    },

    toggle: function (productData) {
      if (!productData || !productData.id) return false;
      const strId = String(productData.id);
      let items = this.getItems();
      const existingIdx = items.findIndex(function (item) {
        return item.id === strId;
      });
      let isAdded = false;

      if (existingIdx > -1) {
        items.splice(existingIdx, 1);
        isAdded = false;
      } else {
        items.unshift({
          id: strId,
          handle: productData.handle || '',
          title: productData.title || 'Fine Jewelry Piece',
          price: productData.price || '',
          compare_at_price: productData.compare_at_price || '',
          image: productData.image || '',
          url: productData.url || (productData.handle ? '/products/' + productData.handle : ''),
          variantId: productData.variantId || ''
        });
        isAdded = true;
      }

      this.saveItems(items);
      this.notify(isAdded, strId, productData);
      return isAdded;
    },

    remove: function (productId) {
      const strId = String(productId);
      let items = this.getItems().filter(function (item) {
        return item.id !== strId && item.handle !== strId;
      });
      this.saveItems(items);
      this.notify(false, strId, { id: strId });
    },

    clear: function () {
      this.saveItems([]);
      this.notify(false, null, null);
    },

    notify: function (isAdded, id, data) {
      const items = this.getItems();
      this.updateBadges(items.length);
      this.syncCardButtons();
      this.renderDrawer();
      this.renderPageGrid();

      window.dispatchEvent(
        new CustomEvent('lucent:wishlist:updated', {
          detail: { items: items, count: items.length, isAdded: isAdded, id: id, data: data }
        })
      );
    },

    updateBadges: function (count) {
      const safeCount = typeof count === 'number' ? count : this.getItems().length;
      document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
        el.textContent = safeCount;
        el.setAttribute('data-count', safeCount);
        if (safeCount > 0) {
          el.style.display = 'inline-flex';
          el.classList.add('pop-animation');
          setTimeout(function () {
            el.classList.remove('pop-animation');
          }, 350);
        } else {
          el.style.display = 'none';
        }
      });
    },

    syncCardButtons: function () {
      const items = this.getItems();
      const ids = new Set(items.map(function (i) { return String(i.id); }));
      document.querySelectorAll('[data-wishlist-button]').forEach(function (btn) {
        const btnId = String(btn.getAttribute('data-wishlist-button') || btn.getAttribute('data-product-id') || '');
        if (ids.has(btnId)) {
          btn.classList.add('is-active');
          btn.setAttribute('aria-pressed', 'true');
        } else {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-pressed', 'false');
        }
      });
    },

    open: function () {
      const drawer = document.getElementById('LucentWishlistDrawer');
      if (drawer) {
        drawer.classList.add('is-open');
        document.body.classList.add('wishlist-drawer-open');
        this.renderDrawer();
      }
    },

    close: function () {
      const drawer = document.getElementById('LucentWishlistDrawer');
      if (drawer) {
        drawer.classList.remove('is-open');
        document.body.classList.remove('wishlist-drawer-open');
      }
    },

    renderDrawer: function () {
      const container = document.getElementById('LucentWishlistDrawerContent');
      const countEl = document.getElementById('LucentWishlistDrawerChip');
      if (!container) return;

      const items = this.getItems();
      if (countEl) {
        countEl.textContent = items.length + (items.length === 1 ? ' piece' : ' pieces');
      }

      if (items.length === 0) {
        container.innerHTML = `
          <div class="lucent-wishlist-empty">
            <div class="lucent-wishlist-empty__icon-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M20.8 8.7C20.8 14.1 12 19.2 12 19.2S3.2 14.1 3.2 8.7A4.7 4.7 0 0 1 12 6.3A4.7 4.7 0 0 1 20.8 8.7Z"></path>
              </svg>
            </div>
            <h3 class="lucent-wishlist-empty__title">Your Wishlist is Empty</h3>
            <p class="lucent-wishlist-empty__desc">Save your favourite diamond designs to easily compare and revisit them anytime.</p>
            <a href="/collections/all" class="lucent-wishlist-empty__btn">
              Explore Collections
            </a>
          </div>
        `;
        return;
      }

      let html = '<ul class="lucent-wishlist-drawer__list">';
      items.forEach(function (item) {
        const imageMarkup = item.image
          ? `<img src="${item.image}" alt="${escapeHtml(item.title)}" loading="lazy" width="84" height="84" />`
          : `<svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#ccc"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"></path></svg>`;

        const compareMarkup = item.compare_at_price
          ? `<span class="lucent-wishlist-item__price--compare">${item.compare_at_price}</span>`
          : '';

        const productUrl = item.url || (item.handle ? '/products/' + item.handle : '#');

        html += `
          <li class="lucent-wishlist-item" data-wishlist-id="${item.id}">
            <a href="${productUrl}" class="lucent-wishlist-item__image-wrap" tabindex="-1">
              ${imageMarkup}
            </a>
            <div class="lucent-wishlist-item__details">
              <div>
                <div class="lucent-wishlist-item__top">
                  <a href="${productUrl}" class="lucent-wishlist-item__title">
                    ${escapeHtml(item.title)}
                  </a>
                  <button type="button" class="lucent-wishlist-item__remove-btn" data-action-remove="${item.id}" title="Remove from Wishlist" aria-label="Remove ${escapeHtml(item.title)}">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
                <div class="lucent-wishlist-item__pricing">
                  <span class="lucent-wishlist-item__price">${item.price || ''}</span>
                  ${compareMarkup}
                </div>
              </div>
              <div class="lucent-wishlist-item__actions">
                ${
                  item.variantId
                    ? `<button type="button" class="lucent-wishlist-item__btn-bag" data-action-add-to-cart="${item.variantId}">Add to Bag</button>`
                    : `<a href="${productUrl}" class="lucent-wishlist-item__btn-bag">View Piece</a>`
                }
                <a href="${productUrl}" class="lucent-wishlist-item__btn-view">Details</a>
              </div>
            </div>
          </li>
        `;
      });
      html += '</ul>';

      container.innerHTML = html;
      this.attachDrawerListeners(container);
    },

    attachDrawerListeners: function (container) {
      const self = this;

      // Remove Buttons
      container.querySelectorAll('[data-action-remove]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          const id = btn.getAttribute('data-action-remove');
          const row = btn.closest('.lucent-wishlist-item');
          if (row) {
            row.classList.add('is-removing');
            setTimeout(function () {
              self.remove(id);
            }, 250);
          } else {
            self.remove(id);
          }
        });
      });

      // Add to Cart
      container.querySelectorAll('[data-action-add-to-cart]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          const variantId = btn.getAttribute('data-action-add-to-cart');
          if (!variantId) return;

          btn.textContent = 'Adding...';
          btn.disabled = true;

          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          })
            .then(function (res) { return res.json(); })
            .then(function (cartItem) {
              btn.textContent = 'Added ✓';
              btn.classList.add('is-added');

              // Refresh Dawn Cart count / drawer if present
              fetch('/cart.js')
                .then(function (r) { return r.json(); })
                .then(function (cart) {
                  document.querySelectorAll('.lj-cart-count, [data-cart-count]').forEach(function (el) {
                    el.textContent = cart.item_count;
                  });
                });

              // If Dawn cart drawer exists, open it
              const cartDrawer = document.querySelector('cart-drawer');
              if (cartDrawer && typeof cartDrawer.open === 'function') {
                setTimeout(function () {
                  self.close();
                  cartDrawer.open();
                }, 400);
              }
            })
            .catch(function (err) {
              console.error('Add to cart error', err);
              btn.textContent = 'Error';
              setTimeout(function () {
                btn.textContent = 'Add to Bag';
                btn.disabled = false;
              }, 1200);
            });
        });
      });
    },

    renderPageGrid: function () {
      const grid = document.querySelector('[data-wishlist-page-grid]');
      const emptyContainer = document.querySelector('[data-wishlist-page-empty]');
      const countEl = document.querySelector('[data-wishlist-page-count]');
      if (!grid) return;

      const items = this.getItems();
      if (countEl) {
        countEl.textContent = items.length + (items.length === 1 ? ' piece' : ' pieces');
      }

      if (items.length === 0) {
        grid.style.display = 'none';
        if (emptyContainer) emptyContainer.style.display = 'flex';
        return;
      }

      grid.style.display = 'grid';
      if (emptyContainer) emptyContainer.style.display = 'none';

      const self = this;
      let html = '';
      items.forEach(function (item) {
        const productUrl = item.url || (item.handle ? '/products/' + item.handle : '#');
        const imageMarkup = item.image
          ? `<img src="${item.image}" alt="${escapeHtml(item.title)}" loading="lazy" />`
          : `<div style="background:#f4f4f4;width:100%;height:100%;display:flex;align-items:center;justify-content:center;"><svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="#ccc"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"></path></svg></div>`;

        html += `
          <div class="premium-product-card" data-product-id="${item.id}">
            <button type="button" class="premium-product-card__wishlist is-active" data-wishlist-page-remove="${item.id}" title="Remove from Wishlist" aria-label="Remove ${escapeHtml(item.title)}">
              <svg class="wishlist-icon" viewBox="0 0 24 24" width="20" height="20">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"></path>
              </svg>
            </button>
            <div class="premium-product-card__media">
              <a href="${productUrl}" class="premium-product-card__media-link">
                ${imageMarkup}
              </a>
            </div>
            <div class="premium-product-card__info">
              <h3 class="premium-product-card__title">
                <a href="${productUrl}">${escapeHtml(item.title)}</a>
              </h3>
              <div class="premium-product-card__price">
                <span class="price-item price-item--regular">${item.price || ''}</span>
                ${item.compare_at_price ? `<span class="price-item price-item--compare">${item.compare_at_price}</span>` : ''}
              </div>
              <div style="margin-top: 12px; display: flex; gap: 8px;">
                ${
                  item.variantId
                    ? `<button type="button" class="lucent-wishlist-item__btn-bag" data-action-add-to-cart="${item.variantId}">Add to Bag</button>`
                    : `<a href="${productUrl}" class="lucent-wishlist-item__btn-bag">View Piece</a>`
                }
              </div>
            </div>
          </div>
        `;
      });

      grid.innerHTML = html;

      grid.querySelectorAll('[data-wishlist-page-remove]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          const id = btn.getAttribute('data-wishlist-page-remove');
          self.remove(id);
        });
      });

      this.attachDrawerListeners(grid);
    },

    init: function () {
      const self = this;

      // Update badge counts immediately on load
      this.updateBadges();
      this.syncCardButtons();
      this.renderPageGrid();

      // Wishlist triggers (header heart, mobile menu wishlist, etc.)
      document.addEventListener('click', function (e) {
        const trigger = e.target.closest('[data-wishlist-drawer-trigger], .lj-action--heart');
        if (trigger) {
          e.preventDefault();
          self.open();
        }

        // Close triggers
        if (
          e.target.matches('[data-wishlist-drawer-close]') ||
          e.target.closest('[data-wishlist-drawer-close]') ||
          e.target.classList.contains('lucent-wishlist-drawer__overlay')
        ) {
          e.preventDefault();
          self.close();
        }

        // Clear all button
        if (e.target.matches('[data-wishlist-clear]') || e.target.closest('[data-wishlist-clear]')) {
          e.preventDefault();
          if (confirm('Are you sure you want to clear your entire wishlist?')) {
            self.clear();
          }
        }
      });

      // Escape key to close
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          self.close();
        }
      });

      // Re-sync if other tabs change localStorage
      window.addEventListener('storage', function (e) {
        if (e.key === STORAGE_KEY) {
          self.updateBadges();
          self.syncCardButtons();
          self.renderDrawer();
          self.renderPageGrid();
        }
      });

      // Auto-open drawer if landing on /pages/wishlist when no dedicated page template is rendered
      if (
        window.location.pathname.indexOf('/pages/wishlist') !== -1 &&
        !document.querySelector('[data-wishlist-page-grid]')
      ) {
        setTimeout(function () {
          self.open();
        }, 150);
      }
    }
  };

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      window.LucentWishlist.init();
    });
  } else {
    window.LucentWishlist.init();
  }
})();
